import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Bot, Building2, CircleDot } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
    FilterBar,
    type FilterChip,
    type FilterDimension,
    type FilterOption,
} from '@apps/web/components/ui/filter_bar';
import { Spinner } from '@apps/web/components/ui/spinner';
import { formatDelimitedLabel } from '@apps/web/lib/format_utils';
import { AgentTraceViewTabs } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_view_tabs';
import { EphemeralRunTable } from '@apps/web/routes/_app/admin/agent-traces/ephemeral_run_table';
import {
    type AdminAgentTraceRunsParams,
    useAdminAgentTraceRuns,
    useAllAdminOrganizations,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgentTraceRunCursorRead,
    type AgentTraceRunListRow,
} from '@libraries/typescript/api_client';

const PAGE_SIZE = 50;
const RUN_STATUSES = [
    'idle',
    'running',
    'failed',
    'completed',
    'cancelled',
] as const satisfies ReadonlyArray<AgentTraceRunListRow['status']>;

type EphemeralFilterDimensionKey = 'organization' | 'status';
type EphemeralFilterChip = FilterChip<EphemeralFilterDimensionKey>;
type EphemeralFilterDimension = FilterDimension<EphemeralFilterDimensionKey>;

export const Route = createFileRoute('/_app/admin/agent-traces/ephemeral')({
    component: EphemeralAgentTracesPage,
    head: () => ({ meta: [{ title: 'Ephemeral Agent Traces' }] }),
});

function isRunStatus(value: string): value is AgentTraceRunListRow['status'] {
    return RUN_STATUSES.some((status) => status === value);
}

function mergeRunsById(
    currentRuns: Array<AgentTraceRunListRow>,
    nextRuns: Array<AgentTraceRunListRow>,
): Array<AgentTraceRunListRow> {
    const currentRunIds = new Set(currentRuns.map((run) => run.run_id));
    return [
        ...currentRuns,
        ...nextRuns.filter((run) => !currentRunIds.has(run.run_id)),
    ];
}

export function EphemeralAgentTracesPage() {
    const router = useRouter();
    const [filterChips, setFilterChips] = useState<Array<EphemeralFilterChip>>(
        [],
    );
    const [loadMoreCursor, setLoadMoreCursor] =
        useState<AgentTraceRunCursorRead | null>(null);
    const [olderRuns, setOlderRuns] = useState<Array<AgentTraceRunListRow>>([]);
    const [olderNextCursor, setOlderNextCursor] = useState<
        AgentTraceRunCursorRead | null | undefined
    >(undefined);
    const [loadMoreFailed, setLoadMoreFailed] = useState(false);

    // Match the Conversations filter: load every organization and show the
    // internal name when one exists so admins see the same cross-org choices.
    const { organizations } = useAllAdminOrganizations({
        autoLoadPages: true,
        orgDataOnly: true,
    });
    const organizationOptions = useMemo<Array<FilterOption>>(
        () =>
            [...organizations]
                .sort((left, right) =>
                    (left.internal_name ?? left.name).localeCompare(
                        right.internal_name ?? right.name,
                    ),
                )
                .map((organization) => ({
                    value: organization.id,
                    label: organization.internal_name ?? organization.name,
                })),
        [organizations],
    );
    const filterDimensions = useMemo<Array<EphemeralFilterDimension>>(
        () => [
            {
                key: 'organization',
                label: 'Organization',
                operators: ['is', 'is_not'],
                icon: Building2,
                options: organizationOptions,
                multiSelect: true,
                searchable: true,
            },
            {
                key: 'status',
                label: 'Status',
                operators: ['is'],
                icon: CircleDot,
                options: RUN_STATUSES.map((status) => ({
                    value: status,
                    label: formatDelimitedLabel(status),
                })),
                multiSelect: true,
            },
        ],
        [organizationOptions],
    );
    const queryParams = useMemo<AdminAgentTraceRunsParams>(() => {
        const includedOrganizationIds = filterChips.find(
            (chip) =>
                chip.dimension === 'organization' &&
                (chip.operator ?? 'is') === 'is',
        )?.values;
        const excludedOrganizationIds = filterChips.find(
            (chip) =>
                chip.dimension === 'organization' && chip.operator === 'is_not',
        )?.values;
        const statuses = filterChips
            .find((chip) => chip.dimension === 'status')
            ?.values.filter(isRunStatus);
        return {
            limit: PAGE_SIZE,
            organization_ids:
                includedOrganizationIds && includedOrganizationIds.length > 0
                    ? includedOrganizationIds
                    : undefined,
            exclude_organization_ids:
                excludedOrganizationIds && excludedOrganizationIds.length > 0
                    ? excludedOrganizationIds
                    : undefined,
            statuses: statuses && statuses.length > 0 ? statuses : undefined,
        };
    }, [filterChips]);
    const {
        data: firstPage,
        error: firstPageError,
        isLoading: isLoadingFirstPageRequest,
    } = useAdminAgentTraceRuns(queryParams, { cursor: null });
    const {
        data: loadMorePage,
        error: loadMoreError,
        isLoading: isLoadingMore,
        isValidating: isValidatingMore,
    } = useAdminAgentTraceRuns(queryParams, {
        cursor: loadMoreCursor,
        enabled: loadMoreCursor !== null,
    });

    useEffect(() => {
        if (!loadMoreCursor || !loadMorePage) {
            return;
        }
        setOlderRuns((currentRuns) =>
            mergeRunsById(currentRuns, loadMorePage.runs),
        );
        setOlderNextCursor(loadMorePage.next_cursor);
        setLoadMoreFailed(false);
        setLoadMoreCursor(null);
    }, [loadMoreCursor, loadMorePage]);

    useEffect(() => {
        if (!loadMoreCursor || !loadMoreError) {
            return;
        }
        setLoadMoreFailed(true);
        setLoadMoreCursor(null);
    }, [loadMoreCursor, loadMoreError]);

    function handleFilterChipsChange(nextChips: Array<EphemeralFilterChip>) {
        setFilterChips(nextChips);
        setLoadMoreCursor(null);
        setOlderRuns([]);
        setOlderNextCursor(undefined);
        setLoadMoreFailed(false);
    }

    function handleSelectRun(runId: string) {
        void router.navigate({
            to: '/admin/agent-traces/run/$runId',
            params: { runId },
        });
    }

    const runs = mergeRunsById(firstPage?.runs ?? [], olderRuns);
    const nextCursor =
        olderNextCursor === undefined
            ? (firstPage?.next_cursor ?? null)
            : olderNextCursor;
    const isLoadingFirstPage = isLoadingFirstPageRequest && runs.length === 0;
    const isLoadingMorePage =
        loadMoreCursor !== null &&
        (isLoadingMore || isValidatingMore) &&
        !loadMoreError;

    return (
        <div className="flex flex-col gap-4 p-6">
            <AgentTraceViewTabs activeView="ephemeral" />
            <div className="flex flex-wrap items-center gap-2">
                <Bot className="h-5 w-5" />
                <h1 className="text-lg font-semibold">Ephemeral Runs</h1>
            </div>
            <FilterBar
                dimensions={filterDimensions}
                chips={filterChips}
                onChange={handleFilterChipsChange}
            />
            {isLoadingFirstPage ? (
                <div
                    role="status"
                    className="text-muted-foreground flex items-center justify-center gap-2 py-12 text-sm"
                >
                    <Spinner size="xl" tone="muted" />
                    Loading ephemeral runs…
                </div>
            ) : firstPageError && runs.length === 0 ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    Couldn’t load ephemeral runs. Refresh the page to try again.
                </div>
            ) : runs.length === 0 ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    No ephemeral runs found.
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {loadMoreFailed ? (
                        <div className="text-destructive text-center text-sm">
                            Couldn’t load more runs. Try again.
                        </div>
                    ) : null}
                    <EphemeralRunTable
                        runs={runs}
                        nextCursor={nextCursor}
                        isLoadingMore={isLoadingMorePage}
                        onLoadMore={(cursor) => {
                            setLoadMoreFailed(false);
                            setLoadMoreCursor(cursor);
                        }}
                        onSelectRun={handleSelectRun}
                    />
                </div>
            )}
        </div>
    );
}
